import { useState, useEffect, useCallback } from 'react';
import { Send, Trash2, Loader2, User, AlertCircle, Clock, Share2 } from 'lucide-react';
import { notify } from '@/lib/notify';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ShareSessionDialog } from './share-session-dialog';

interface TelegramSession {
  id: number;
  telegramUserId: string | null;
  telegramUsername: string | null;
  telegramFirstName: string | null;
  telegramLastName: string | null;
  telegramPhone: string | null;
  status: string;
  isActive: boolean;
  lastSyncAt: string | null;
  isOwner?: boolean;
  isShared?: boolean;
  ownerName?: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

function renderSessionIcon(session: TelegramSession, pending: boolean, isActivating: boolean) {
  if (pending) {
    return session.status === 'ERROR' ? (
      <AlertCircle className="h-4 w-4 flex-shrink-0 text-destructive" />
    ) : (
      <Clock className="h-4 w-4 flex-shrink-0 text-yellow-500" />
    );
  }
  if (isActivating) {
    return <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-muted-foreground" />;
  }
  return <User className="h-4 w-4 flex-shrink-0 text-muted-foreground" />;
}

function renderSessionSubtitle(session: TelegramSession, statusLabel: string | null) {
  if (statusLabel) {
    return (
      <span className={cn(
        'truncate text-xs',
        session.status === 'ERROR' ? 'text-destructive' : 'text-yellow-500'
      )}>
        {statusLabel}
      </span>
    );
  }
  if (session.isShared && session.ownerName) {
    return <span className="truncate text-xs text-muted-foreground">от @{session.ownerName}</span>;
  }
  if (session.telegramUsername && session.telegramFirstName) {
    return <span className="truncate text-xs text-muted-foreground">@{session.telegramUsername}</span>;
  }
  return null;
}

export function TelegramSessionsMenu() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<TelegramSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<TelegramSession | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activatingId, setActivatingId] = useState<number | null>(null);
  const [sessionToShare, setSessionToShare] = useState<TelegramSession | null>(null);

  const isPendingAuth = (session: TelegramSession) => {
    return ['WAITING_CODE', 'WAITING_PASSWORD', 'WAITING_QR', 'ERROR'].includes(session.status);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'WAITING_CODE': return 'Ожидает код';
      case 'WAITING_PASSWORD': return 'Ожидает 2FA';
      case 'WAITING_QR': return 'Ожидает QR';
      case 'ERROR': return 'Ошибка';
      default: return null;
    }
  };

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/accounts`);

      const result: ApiResponse<TelegramSession[]> = await response.json();
      if (result.success && result.data) {
        setSessions(result.data);
      }
    } catch {
      // ignore
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleDeleteSession = async () => {
    if (!sessionToDelete?.id) {return;}

    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/accounts/${sessionToDelete.id}`, {
        method: 'DELETE',
      });

      const result: ApiResponse<null> = await response.json();

      if (!result.success) {
        notify.error(result.error || 'Ошибка при удалении сессии');
        return;
      }

      notify.success('Telegram сессия удалена');
      await fetchSessions();
    } catch {
      notify.error('Ошибка сети');
    } finally {
      setIsDeleting(false);
      setSessionToDelete(null);
    }
  };

  const handleActivate = async (session: TelegramSession) => {
    if (session.isActive || isPendingAuth(session)) {return;}
    setActivatingId(session.id);
    try {
      const response = await fetch(`${API_URL}/api/telegram-client/accounts/${session.id}/activate`, {
        method: 'POST',
      });
      const result: ApiResponse<unknown> = await response.json();
      if (!result.success) {
        notify.error(result.error || 'Не удалось переключить сессию');
        return;
      }
      notify.success('Сессия выбрана');
      await fetchSessions();
    } catch {
      notify.error('Ошибка сети');
    } finally {
      setActivatingId(null);
    }
  };

  const formatSessionName = (session: TelegramSession) => {
    if (session.telegramFirstName || session.telegramLastName) {
      return [session.telegramFirstName, session.telegramLastName].filter(Boolean).join(' ');
    }
    if (session.telegramUsername) {return `@${session.telegramUsername}`;}
    if (session.telegramPhone) {return session.telegramPhone;}
    return 'Telegram аккаунт';
  };

  const renderSessionItem = (session: TelegramSession) => {
    const statusLabel = getStatusLabel(session.status);
    const pending = isPendingAuth(session);
    const isActivating = activatingId === session.id;
    const owned = session.isOwner !== false; // undefined трактуем как свою (бэкомпат)

    return (
      <DropdownMenuItem
        key={session.id}
        className="flex items-center justify-between gap-2"
        onSelect={(e) => {
          if (pending) {
            // Переход на страницу telegram-client для продолжения авторизации
            navigate('/telegram-client');
          } else {
            e.preventDefault();
            handleActivate(session);
          }
        }}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {renderSessionIcon(session, pending, isActivating)}
          <div className="flex flex-col min-w-0">
            <span className="truncate text-sm">{formatSessionName(session)}</span>
            {renderSessionSubtitle(session, statusLabel)}
          </div>
          {session.isActive && !pending && (
            <span className="ml-auto text-xs text-green-500 flex-shrink-0">●</span>
          )}
        </div>
        {owned && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 flex-shrink-0"
              title="Поделиться сессией"
              onClick={(e) => {
                e.stopPropagation();
                setSessionToShare(session);
              }}
            >
              <Share2 className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 flex-shrink-0"
              title="Удалить сессию"
              onClick={(e) => {
                e.stopPropagation();
                setSessionToDelete(session);
              }}
            >
              <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
            </Button>
          </>
        )}
      </DropdownMenuItem>
    );
  };

  const ownSessions = sessions.filter((s) => s.isOwner !== false);
  const sharedSessions = sessions.filter((s) => s.isOwner === false);

  const renderMenuContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (sessions.length === 0) {
      return (
        <div className="px-2 py-4 text-center text-sm text-muted-foreground">
          Нет активных сессий
        </div>
      );
    }
    return (
      <>
        {ownSessions.length > 0 && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground">Мои</DropdownMenuLabel>
            {ownSessions.map(renderSessionItem)}
          </>
        )}
        {sharedSessions.length > 0 && (
          <>
            {ownSessions.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs text-muted-foreground">Доступные мне</DropdownMenuLabel>
            {sharedSessions.map(renderSessionItem)}
          </>
        )}
      </>
    );
  };

  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Send className="mr-2 h-4 w-4" />
          <span>Telegram сессии</span>
          {isLoading && <Loader2 className="ml-auto h-3 w-3 animate-spin" />}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-64">
          {renderMenuContent()}
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      {sessionToShare && (
        <ShareSessionDialog
          sessionId={sessionToShare.id}
          sessionName={formatSessionName(sessionToShare)}
          open={!!sessionToShare}
          onOpenChange={(open) => !open && setSessionToShare(null)}
        />
      )}

      <Dialog open={!!sessionToDelete} onOpenChange={(open) => !open && setSessionToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить Telegram сессию?</DialogTitle>
            <DialogDescription>
              Вы действительно хотите удалить сессию{' '}
              <span className="font-medium">
                {sessionToDelete && formatSessionName(sessionToDelete)}
              </span>
              ? Вам потребуется заново пройти авторизацию для этого аккаунта.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSessionToDelete(null)}
              disabled={isDeleting}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteSession}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Удалить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
